import React, { useState, useEffect, useMemo } from 'react';
import './DailyCalendar.css';

function DailyCalendar({ completedTrades }) {
  // Calculate date range from completed trades
  const dateRange = useMemo(() => {
    if (!completedTrades || completedTrades.length === 0) {
      return { min: null, max: null };
    }

    const dates = completedTrades
      .map(trade => {
        const date = trade.tradeDate || trade.date;
        return date ? new Date(date) : null;
      })
      .filter(date => date !== null && !isNaN(date.getTime()));

    if (dates.length === 0) {
      return { min: null, max: null };
    }

    const minDate = new Date(Math.min(...dates.map(d => d.getTime())));
    const maxDate = new Date(Math.max(...dates.map(d => d.getTime())));

    // Set to first day of month for min, last day for max
    minDate.setDate(1);
    maxDate.setDate(1); // Start of max month

    return { min: minDate, max: maxDate };
  }, [completedTrades]);

  // Initialize currentDate to the minimum trade date (or current month if no trades)
  const [currentDate, setCurrentDate] = useState(() => {
    return dateRange.min ? new Date(dateRange.min) : new Date();
  });

  // Update currentDate when dateRange changes (but only if dateRange is valid)
  useEffect(() => {
    if (dateRange.min) {
      // Set to min date on first load
      setCurrentDate(prev => {
        // Only update if prev is before min or way after max
        const prevMonth = new Date(prev.getFullYear(), prev.getMonth(), 1);
        if (prevMonth < dateRange.min || (dateRange.max && prevMonth > dateRange.max)) {
          return new Date(dateRange.min);
        }
        return prev;
      });
    }
  }, [dateRange.min?.getTime(), dateRange.max?.getTime()]);

  // Calculate daily P&L from completed trades
  const dailyPnL = {};
  if (completedTrades && completedTrades.length > 0) {
    completedTrades.forEach(trade => {
      const date = trade.tradeDate || trade.date || new Date().toISOString().split('T')[0];
      if (!dailyPnL[date]) {
        dailyPnL[date] = { pnl: 0, trades: 0 };
      }
      dailyPnL[date].pnl += trade.pnl || 0;
      dailyPnL[date].trades += 1;
    });
  }

  // Generate calendar for a given month
  const generateCalendar = (year, month) => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const calendarDays = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      calendarDays.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      calendarDays.push({
        day,
        date: dateStr,
        pnl: dailyPnL[dateStr]?.pnl || null,
        trades: dailyPnL[dateStr]?.trades || 0
      });
    }

    return calendarDays;
  };

  // Get months to display (current month and next 2 months)
  // Calculate the correct start month to ensure we can always show 3 months
  const calculateDisplayStart = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    if (!dateRange.min || !dateRange.max) {
      return { year, month };
    }
    
    const currentMonthDate = new Date(year, month, 1);
    const maxAllowedStart = new Date(dateRange.max);
    maxAllowedStart.setMonth(maxAllowedStart.getMonth() - 2); // Can start up to 2 months before max
    
    // Clamp the start month to ensure 3 months are within range
    if (currentMonthDate < dateRange.min) {
      return { year: dateRange.min.getFullYear(), month: dateRange.min.getMonth() };
    } else if (currentMonthDate > maxAllowedStart) {
      return { year: maxAllowedStart.getFullYear(), month: maxAllowedStart.getMonth() };
    }
    
    return { year, month };
  }, [currentDate, dateRange.min, dateRange.max]);
  
  // Sync currentDate if it needs to be adjusted (only when displayStart changes, not when currentDate changes)
  useEffect(() => {
    const { year: displayYear, month: displayMonth } = calculateDisplayStart;
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();
    
    if (displayYear !== currentYear || displayMonth !== currentMonth) {
      const adjustedDate = new Date(displayYear, displayMonth, 1);
      const currentMonthDate = new Date(currentYear, currentMonth, 1);
      // Only update if different to avoid loops
      if (adjustedDate.getTime() !== currentMonthDate.getTime()) {
        setCurrentDate(adjustedDate);
      }
    }
  }, [calculateDisplayStart, currentDate]);
  
  // Generate exactly 3 months - all should be valid after adjustment
  const { year: displayYear, month: displayMonth } = calculateDisplayStart;
  const months = [];
  
  for (let i = 0; i < 3; i++) {
    const m = (displayMonth + i) % 12;
    const y = displayYear + Math.floor((displayMonth + i) / 12);
    const monthDate = new Date(y, m, 1);
    
    // After adjustment, all months should be valid - just double check
    if (dateRange.min && dateRange.max) {
      // Use getTime() for proper comparison
      if (monthDate.getTime() >= dateRange.min.getTime() && monthDate.getTime() <= dateRange.max.getTime()) {
        months.push({
          year: y,
          month: m,
          calendarDays: generateCalendar(y, m)
        });
      }
    } else {
      // No date range restriction - include all months
      months.push({
        year: y,
        month: m,
        calendarDays: generateCalendar(y, m)
      });
    }
  }

  // Navigate months - restricted to trade date range
  const prevMonths = () => {
    const newDate = new Date(displayYear, displayMonth - 3, 1);
    if (dateRange.min && newDate.getTime() >= dateRange.min.getTime()) {
      setCurrentDate(newDate);
    } else if (dateRange.min) {
      // If we'd go before min, jump to min
      setCurrentDate(new Date(dateRange.min));
    }
  };

  const nextMonths = () => {
    const newDate = new Date(displayYear, displayMonth + 3, 1);
    if (dateRange.max) {
      const maxAllowedStart = new Date(dateRange.max);
      maxAllowedStart.setMonth(maxAllowedStart.getMonth() - 2); // Can start up to 2 months before max
      
      if (newDate.getTime() <= maxAllowedStart.getTime()) {
        setCurrentDate(newDate);
      } else {
        // If we'd go after max, go to the maximum allowed start position
        setCurrentDate(new Date(maxAllowedStart));
      }
    } else {
      setCurrentDate(newDate);
    }
  };

  // Check if navigation buttons should be disabled
  const canGoPrev = !dateRange.min || new Date(displayYear, displayMonth - 3, 1).getTime() >= dateRange.min.getTime();
  const maxAllowedStartForCheck = dateRange.max ? (() => {
    const d = new Date(dateRange.max);
    d.setMonth(d.getMonth() - 2);
    return d;
  })() : null;
  const canGoNext = !dateRange.max || !maxAllowedStartForCheck || new Date(displayYear, displayMonth + 3, 1).getTime() <= maxAllowedStartForCheck.getTime();

  const monthNames = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];

  const weekDays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  if (!dateRange.min || !dateRange.max) {
    return (
      <div className="daily-calendar">
        <div className="card">
          <p>No trade dates available to display calendar.</p>
        </div>
      </div>
    );
  }

  const startMonthName = months.length > 0 ? monthNames[months[0].month] : monthNames[displayMonth];
  const startYear = months.length > 0 ? months[0].year : displayYear;
  const endMonthName = months.length > 0 && months[months.length - 1] 
    ? monthNames[months[months.length - 1].month] 
    : monthNames[(displayMonth + 2) % 12];
  const endYear = months.length > 0 && months[months.length - 1]
    ? months[months.length - 1].year
    : displayYear + Math.floor((displayMonth + 2) / 12);

  return (
    <div className="daily-calendar">
      <div className="calendar-header">
        <button 
          onClick={prevMonths} 
          className={`calendar-nav-btn ${!canGoPrev ? 'disabled' : ''}`}
          disabled={!canGoPrev}
        >
          ‹‹
        </button>
        <h3>{startMonthName} {startYear} - {endMonthName} {endYear}</h3>
        <button 
          onClick={nextMonths} 
          className={`calendar-nav-btn ${!canGoNext ? 'disabled' : ''}`}
          disabled={!canGoNext}
        >
          ››
        </button>
      </div>
      
      <div className="calendar-months-container">
        {months.map((monthData, monthIndex) => (
          <div key={`${monthData.year}-${monthData.month}`} className="calendar-month">
            <div className="calendar-month-title">
              {monthNames[monthData.month]} {monthData.year}
            </div>
            <div className="calendar-grid">
              {weekDays.map(day => (
                <div key={day} className="calendar-weekday">
                  {day}
                </div>
              ))}
              
              {monthData.calendarDays.map((dayData, index) => {
                if (!dayData) {
                  return <div key={`empty-${index}`} className="calendar-day empty"></div>;
                }

                const isProfit = dayData.pnl !== null && dayData.pnl > 0;
                const isLoss = dayData.pnl !== null && dayData.pnl < 0;
                const isBreakEven = dayData.pnl !== null && Math.abs(dayData.pnl) < 0.01;

                return (
                  <div
                    key={dayData.date}
                    className={`calendar-day ${isProfit ? 'profit' : ''} ${isLoss ? 'loss' : ''} ${isBreakEven ? 'break-even' : ''} ${dayData.pnl !== null ? 'has-trade' : ''}`}
                    title={dayData.pnl !== null ? `P/L: ₹${dayData.pnl.toFixed(2)} | Trades: ${dayData.trades}` : ''}
                  >
                    <div className="calendar-day-number">{dayData.day}</div>
                    {dayData.pnl !== null && (
                      <div className={`calendar-day-pnl ${isProfit ? 'profit-text' : isLoss ? 'loss-text' : 'break-even-text'}`}>
                        ₹{Math.abs(dayData.pnl).toFixed(0)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="calendar-legend">
        <div className="legend-item">
          <div className="legend-color profit"></div>
          <span>Profit</span>
        </div>
        <div className="legend-item">
          <div className="legend-color loss"></div>
          <span>Loss</span>
        </div>
        <div className="legend-item">
          <div className="legend-color break-even"></div>
          <span>Break Even</span>
        </div>
      </div>
    </div>
  );
}

export default DailyCalendar;
